import { useState } from "react"

import { useTranslation } from "react-i18next"

import { type ServerLogEntry, type ServerLogs } from "@/api/feedback-api"
import { Check, ChevronDown, ChevronRight, Copy, ServerCog } from "@/components/icons"
import { STATUS_TONES } from "@/components/ui/tone-classes"
import { cn } from "@/lib/utils"

/**
 * Readable renderer for the `server_logs` payload captured on a feedback
 * record at submit time (Wave 2). Renders each entry as a compact monospace
 * line — `timestamp · severity · logger · message` — with an expandable
 * stack summary and a copy affordance for the correlation id.
 *
 * Privacy: the backend already sanitizes these lines (formatted message only,
 * no raw args / prompts / document contents; stack summaries are frames-only).
 * This view only displays what was captured — it never fetches more.
 *
 * Back-compat: `value` is optional. Records submitted before server-log capture
 * shipped lack the field entirely — we render a neutral "not captured" notice.
 * When the field is present but `entries` is empty, the backend always supplies
 * an `unavailable_reason`, which we surface verbatim.
 */
export function ServerLogsView({ value }: { value: ServerLogs | null | undefined }) {
  const { t } = useTranslation()
  const entries = Array.isArray(value?.entries) ? value.entries : []

  if (entries.length === 0) {
    let reason: string
    if (value && typeof value.unavailable_reason === "string" && value.unavailable_reason) {
      // Backend told us why (e.g. "no correlation key on report").
      reason = value.unavailable_reason
    } else if (!value) {
      // Old record predating server-log capture — the field is absent.
      reason = t("adminDashboard.feedback.detail.serverLogsAbsent", "Not captured for this report.")
    } else {
      reason = t(
        "adminDashboard.feedback.detail.serverLogsEmpty",
        "No server logs captured for this report."
      )
    }
    return <p className="text-muted-foreground text-xs italic">{reason}</p>
  }

  return (
    <div className="space-y-2">
      <p className="text-muted-foreground text-xs">
        {t("adminDashboard.feedback.detail.serverLogsCount", {
          defaultValue: "{{count}} log line",
          defaultValue_other: "{{count}} log lines",
          count: entries.length,
        })}
      </p>
      {value?.unavailable_reason ? (
        // Present alongside entries when the slice was truncated to fit budget.
        <p className="text-muted-foreground text-[11px] italic">{value.unavailable_reason}</p>
      ) : null}
      <ol className="divide-border bg-muted/40 divide-y overflow-hidden rounded-md border font-mono text-[11px]">
        {entries.map((entry, idx) => (
          <LogRow key={idx} entry={entry} />
        ))}
      </ol>
    </div>
  )
}

function LogRow({ entry }: { entry: ServerLogEntry }) {
  const [expanded, setExpanded] = useState(false)
  const severity = (entry.severity ?? "").toUpperCase()
  const hasStack = typeof entry.stack_summary === "string" && entry.stack_summary.length > 0
  const correlationId =
    entry.correlation_id && entry.correlation_id !== "-" ? entry.correlation_id : null

  return (
    <li className="px-2.5 py-1.5">
      <div className="flex items-start gap-2">
        {hasStack ? (
          <button
            aria-expanded={expanded}
            className="hover:text-foreground text-muted-foreground mt-0.5 shrink-0"
            type="button"
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
          </button>
        ) : (
          <span aria-hidden className="mt-0.5 size-3 shrink-0" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span className="text-muted-foreground shrink-0 tabular-nums">
              {formatTimestamp(entry.timestamp)}
            </span>
            <SeverityBadge severity={severity} />
            {entry.logger ? (
              <span className="text-muted-foreground/80 max-w-[40%] truncate" title={entry.logger}>
                {entry.logger}
              </span>
            ) : null}
          </div>
          {entry.message ? (
            <p className="text-foreground mt-0.5 break-words whitespace-pre-wrap">
              {entry.message}
            </p>
          ) : null}
          {entry.exception_type ? (
            <p className="text-destructive mt-0.5 break-words">{entry.exception_type}</p>
          ) : null}
          {correlationId ? (
            <div className="mt-1">
              <CopyableMono text={correlationId} />
            </div>
          ) : null}
          {hasStack && expanded ? (
            <pre className="bg-muted/60 text-muted-foreground mt-1 overflow-auto rounded p-2 text-[10px] leading-snug">
              {entry.stack_summary}
            </pre>
          ) : null}
        </div>
      </div>
    </li>
  )
}

/**
 * Severity affordance. Colors follow DESIGN.md semantics: destructive (red) for
 * errors/critical, warning (amber) for warnings, muted for info/debug. Tinted
 * surfaces use the `-foreground` text token (calibrated for both light/dark).
 */
function SeverityBadge({ severity }: { severity: string }) {
  if (!severity) return null
  const styles = severityStyles(severity)
  return (
    <span
      className={cn(
        "shrink-0 rounded-md px-1.5 py-0 text-[10px] font-semibold tracking-wide uppercase",
        styles
      )}
    >
      {severity}
    </span>
  )
}

function severityStyles(severity: string): string {
  switch (severity) {
    case "CRITICAL":
    case "FATAL":
    case "ERROR":
      return "border border-destructive bg-destructive/10 text-destructive"
    case "WARNING":
    case "WARN":
      return "border border-warning bg-warning/10 text-warning-foreground"
    case "INFO":
      return STATUS_TONES.info.soft
    default:
      return "border border-border bg-muted text-muted-foreground"
  }
}

/**
 * Local copy of the dialog's `CopyableMono` affordance so the server-logs view
 * is self-contained. Click to copy the correlation id to the clipboard.
 */
function CopyableMono({ text }: { text: string }) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  const handleCopy = () => {
    // Clipboard API is unavailable in non-secure contexts and can reject on
    // permission denial; copy is a best-effort affordance, so guard + swallow.
    // (DOM types say `navigator.clipboard` is always present, but it isn't —
    // cast to the honestly-nullable type so the guard isn't flagged as dead.)
    const clipboard = navigator.clipboard as Clipboard | undefined
    if (!clipboard?.writeText) return
    void clipboard
      .writeText(text)
      .then(() => {
        setCopied(true)
        window.setTimeout(() => setCopied(false), 1500)
      })
      .catch(() => {
        // ignore — never surface a UI error for a copy affordance.
      })
  }
  return (
    <button
      className="hover:bg-accent/40 bg-background inline-flex max-w-full min-w-0 items-center gap-1 rounded border px-1.5 py-0.5 text-[10px]"
      title={t("common.copy", "Copy")}
      type="button"
      onClick={handleCopy}
    >
      <span className="text-muted-foreground/70">corr</span>
      <span className="truncate">{text}</span>
      {copied ? (
        <Check className="text-success size-3 shrink-0" />
      ) : (
        <Copy className="text-muted-foreground size-3 shrink-0" />
      )}
    </button>
  )
}

export function ServerLogsTitleIcon() {
  return <ServerCog className="size-4" />
}

function formatTimestamp(value: string | undefined): string {
  if (!value) return "—"
  try {
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return value
    // Render in UTC: the entry contract and App Insights/KQL are all UTC, so
    // showing the admin's local time would make cross-referencing timelines
    // error-prone. Suffix "Z" to make the zone explicit.
    return `${d.toISOString().slice(11, 19)}Z`
  } catch {
    return value
  }
}
